import { ONBOARDING_CHECKLIST_VERSION } from "../components/OnboardingPage/constants";

export interface NormalizedOnboardingInfo {
  completedStepIds: number[];
  projectId?: string | number | null;
  checklistVersion?: string;
  lastUpdatedAt?: string;
}

type RawOnboardingInfo =
  | string
  | null
  | undefined
  | Partial<NormalizedOnboardingInfo>
  | Record<string, unknown>;

const parseRawInfo = (raw: RawOnboardingInfo): Record<string, unknown> => {
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        return parsed as Record<string, unknown>;
      }
      return {};
    } catch (error) {
      return {};
    }
  }
  if (raw && typeof raw === "object") {
    return raw as Record<string, unknown>;
  }
  return {};
};

const toNumericStepIds = (value: unknown): number[] => {
  const candidates: unknown[] = Array.isArray(value)
    ? value
    : Array.isArray((value as any)?.completedStepIds)
    ? ((value as any).completedStepIds as unknown[])
    : [];
  const unique = new Set<number>();
  candidates.forEach((candidate) => {
    const numeric = Number(candidate);
    if (Number.isFinite(numeric) && numeric > 0) {
      unique.add(numeric);
    }
  });
  const results = Array.from(unique);
  if (!results.includes(1)) {
    results.push(1);
  }
  return results.sort((a, b) => a - b);
};

export const normalizeOnboardingInfo = (
  raw: RawOnboardingInfo
): NormalizedOnboardingInfo => {
  const parsed = parseRawInfo(raw);
  const completedStepIds = toNumericStepIds(parsed.completedStepIds ?? parsed.completed_step_ids ?? parsed);
  const projectId =
    parsed.projectId ?? parsed.project_id ?? (parsed as any)?.project ?? null;
  const checklistVersion =
    parsed.checklistVersion ?? parsed.checklist_version ?? undefined;
  const lastUpdatedAt =
    parsed.lastUpdatedAt ?? parsed.last_updated_at ?? undefined;

  return {
    completedStepIds,
    projectId,
    checklistVersion: checklistVersion as string,
    lastUpdatedAt: lastUpdatedAt as string,
  };
};

export const buildOnboardingInfoPayload = (params: {
  completedStepIds: Iterable<number>;
  projectId?: string | number | null;
  checklistVersion?: string;
  lastUpdatedAt?: string;
}): NormalizedOnboardingInfo => {
  const base = {
    completedStepIds: Array.from(params.completedStepIds),
    projectId:
      params.projectId === undefined ? null : (params.projectId as any),
    checklistVersion:
      params.checklistVersion || ONBOARDING_CHECKLIST_VERSION || undefined,
    lastUpdatedAt: params.lastUpdatedAt,
  };
  const normalized = normalizeOnboardingInfo(base);
  normalized.checklistVersion = base.checklistVersion;
  normalized.projectId = base.projectId;
  normalized.lastUpdatedAt =
    base.lastUpdatedAt || new Date().toISOString();
  return normalized;
};

export const onboardingInfoMatchesProject = (
  info: NormalizedOnboardingInfo,
  projectId?: string | number | null
): boolean => {
  if (projectId === null || projectId === undefined || projectId === "") {
    return true;
  }
  if (
    info.projectId === null ||
    info.projectId === undefined ||
    info.projectId === ""
  ) {
    return true;
  }
  return String(info.projectId) === String(projectId);
};

export const coerceBooleanFlag = (value: unknown): boolean => {
  return value === true || value === 1 || value === "1";
};

export const resolveOnboardingProjectId = (
  projects: Array<{
    id?: unknown;
    isSample?: unknown;
    is_sample?: unknown;
    createdAt?: string;
    created_at?: string;
  }> | null | undefined
): number | null => {
  if (!Array.isArray(projects) || !projects.length) {
    return null;
  }

  const nonSampleProjects = projects.filter((project) => {
    const flagSource = project?.isSample ?? (project as any)?.is_sample;
    return !coerceBooleanFlag(flagSource);
  });

  if (!nonSampleProjects.length) {
    return null;
  }

  const sorted = nonSampleProjects
    .map((project) => {
      const createdRaw = project?.createdAt ?? (project as any)?.created_at;
      const timestamp = createdRaw ? new Date(createdRaw).getTime() : NaN;
      return {
        project,
        timestamp: Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER,
      };
    })
    .sort((a, b) => a.timestamp - b.timestamp);

  const firstProject = sorted[0]?.project;
  if (!firstProject || firstProject.id === undefined || firstProject.id === null) {
    return null;
  }

  const numericId = Number(firstProject.id);
  return Number.isFinite(numericId) ? numericId : null;
};
